'use client';

import { useEffect, useLayoutEffect } from 'react';
import { useCurrencyStore } from '@/store/currencyStore';

const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

export default function CurrencyInitializer() {
  const {
    setCurrency,
    setSymbol,
    setTargetCurrencyRate,
    setExchangeRates,
    setBaseCurrency,
    setInitialized,
  } = useCurrencyStore();

  useIsomorphicLayoutEffect(() => {
    async function detectCurrency() {
      try {
        // Check localStorage first (Zustand persist storage)
        const storedData = localStorage.getItem('currency-store');
        if (storedData) {
          try {
            const parsed = JSON.parse(storedData);
            const storedCode = parsed?.state?.currencyCode;
            const storedSymbol = parsed?.state?.currencySymbol;
            const storedRate = Number(parsed?.state?.targetCurrencyRate);
            const storedRates = parsed?.state?.exchangeRates || null;
            const storedBase =
              (parsed?.state?.baseCurrency as string | undefined) || 'USD';

            const hasValidStoredRates =
              storedRates &&
              typeof storedRates === 'object' &&
              Object.keys(storedRates).length > 0;

            const hasValidStoredRate =
              typeof storedRate === 'number' &&
              !Number.isNaN(storedRate) &&
              storedRate > 0 &&
              (storedCode === 'USD' ? storedRate === 1 : storedRate !== 1);

            if (storedCode && storedSymbol && hasValidStoredRate) {
              console.log(
                `✅ Currency loaded from storage: ${storedCode} (hydrating store while refreshing rates)`
              );
              setCurrency(storedCode);
              setSymbol(storedSymbol);
              setTargetCurrencyRate(storedRate);
              if (hasValidStoredRates) {
                setExchangeRates(storedRates);
                setBaseCurrency(storedBase);
                setInitialized(true);
              }
            }

            console.log(
              'ℹ️ Stored currency data incomplete or outdated, running detection again'
            );
          } catch {
            console.warn('Failed to parse stored currency, will detect fresh');
          }
        }

        console.log('🌍 Detecting user location...');

        // Try multiple IP detection services for better reliability
        let countryCode = 'US';

        // Method 1: Try ipinfo.io (if token is available)
        if (process.env.NEXT_PUBLIC_IPINFO_TOKEN) {
          try {
            const res = await fetch(
              `https://ipinfo.io/json?token=${process.env.NEXT_PUBLIC_IPINFO_TOKEN}`
            );
            if (res.ok) {
              const data = await res.json();
              countryCode = data.country?.toUpperCase() || 'US';
              console.log('✅ IPInfo detected country:', countryCode);
            }
          } catch {
            console.warn('⚠️ IPInfo failed, trying alternative...');
          }
        }

        // Method 2: Fallback to ip-api.com (free, no token needed)
        if (countryCode === 'US') {
          try {
            const res = await fetch('https://ipapi.co/json/');
            if (res.ok) {
              const data = await res.json();
              countryCode = data.country_code?.toUpperCase() || 'US';
              console.log('✅ IP-API detected country:', countryCode);
            }
          } catch {
            console.warn('⚠️ IP-API failed, trying final fallback...');
          }
        }

        // Method 3: Last resort - check timezone as hint
        if (countryCode === 'US') {
          const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          if (timezone.includes('Kolkata') || timezone.includes('India')) {
            countryCode = 'IN';
            console.log('✅ Timezone detected India');
          }
        }

        // Fetch exchange rates (base USD)
        let exchangeRates: Record<string, number> | null = null;
        let baseCurrency = 'USD';

        try {
          const rateRes = await fetch(
            'https://api.exchangerate-api.com/v4/latest/USD'
          );
          if (rateRes.ok) {
            const rateData = await rateRes.json();
            const fetchedBase =
              typeof rateData.base === 'string' && rateData.base.length > 0
                ? rateData.base.toUpperCase()
                : 'USD';
            baseCurrency = fetchedBase;

            if (rateData.rates && typeof rateData.rates === 'object') {
              exchangeRates = Object.entries(rateData.rates).reduce<
                Record<string, number>
              >((acc, [code, value]) => {
                const normalizedCode = code.toUpperCase();
                const numericValue = Number(value);
                if (
                  normalizedCode &&
                  !Number.isNaN(numericValue) &&
                  numericValue > 0
                ) {
                  acc[normalizedCode] = numericValue;
                }
                return acc;
              }, {});
            }
          } else {
            console.warn(
              `⚠️ Exchange rate API responded with status ${rateRes.status}`
            );
          }
        } catch (rateError) {
          console.warn(
            '⚠️ Exchange rate API failed, using fallback',
            rateError
          );
        }

        if (!exchangeRates) {
          exchangeRates = {
            USD: 1,
            INR: 83,
          };
          baseCurrency = 'USD';
        } else {
          exchangeRates[baseCurrency] = 1;
          if (!exchangeRates.USD) {
            exchangeRates.USD = 1;
          }
          if (!exchangeRates.INR) {
            exchangeRates.INR = 83;
          }
        }

        const resolveRateFor = (currencyCode: string) => {
          const normalized = currencyCode?.toUpperCase();
          if (!normalized) return 1;
          if (normalized === baseCurrency) return 1;
          const rate = exchangeRates?.[normalized];
          if (typeof rate === 'number' && !Number.isNaN(rate) && rate > 0) {
            return rate;
          }
          if (normalized === 'INR') return 83;
          return 1;
        };

        // Default to USD for all non-India users
        let code = 'USD';
        let symbol = '$';
        let rate = 1; // USD to USD rate is 1

        // India users get INR with conversion
        if (countryCode === 'IN') {
          code = 'INR';
          symbol = '₹';
        } else {
          console.log('🌎 International user - USD prices (no conversion)');
        }

        rate = resolveRateFor(code);

        console.log(
          `💰 Currency: ${code} (${symbol}), Conversion rate: ${rate}, Base: ${baseCurrency}`
        );
        updateCurrency(code, symbol, rate, {
          exchangeRates,
          baseCurrency,
        });
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : JSON.stringify(err);

        console.error(
          '❌ Currency detection failed, defaulting to USD:',
          message
        );
        updateCurrency('USD', '$', 1, {
          exchangeRates: {
            USD: 1,
            INR: 83,
          },
          baseCurrency: 'USD',
        });
      }
    }

    function updateCurrency(
      code: string,
      symbol: string,
      rate: number,
      options?: {
        exchangeRates?: Record<string, number> | null;
        baseCurrency?: string;
      }
    ) {
      // Use batch update to prevent race conditions
      // Set all values at once before marking as initialized
      setCurrency(code);
      setSymbol(symbol);
      setTargetCurrencyRate(rate);
      setExchangeRates(options?.exchangeRates ?? null);
      setBaseCurrency(options?.baseCurrency || 'USD');
      setInitialized(true);
    }

    detectCurrency();
  }, [
    setCurrency,
    setSymbol,
    setTargetCurrencyRate,
    setExchangeRates,
    setBaseCurrency,
    setInitialized,
  ]);

  return null;
}
